import { useRef, useState } from "react";
import { Building2, Check, ChevronsUpDown, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

export const CompanySwitcher = ({ variant = "desktop" }) => {
  const { company, companies, switchCompany } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const requestId = useRef(null);
  const tid = (part) => `company-${variant}-${part}`;
  const select = async (id) => {
    if (id === company?.id) return;
    if (!window.confirm("Switch company? Any unsaved changes on this page will be discarded.")) return;
    setBusy(true);
    try { await switchCompany(id); }
    catch { toast.error("Could not switch company. Please try again."); setBusy(false); }
  };
  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      requestId.current ||= crypto.randomUUID();
      const { data } = await api.post("/founder/companies", { name: name.trim(), org_type: "company", request_id: requestId.current });
      await switchCompany(data.id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create company. Please try again.");
      setBusy(false);
    }
  };
  return <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button disabled={busy} data-testid={tid("switcher")} className="mb-2 flex w-full items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-left transition-colors hover:bg-white/10">
          <Building2 className="h-4 w-4 shrink-0 text-brand" />
          <span className="min-w-0 flex-1"><span data-testid={tid("active-name")} className="block truncate text-sm font-semibold text-white">{company?.name || "Your company"}</span><span data-testid={tid("active-plan")} className="block text-[10px] capitalize text-white/65">{company?.plan || "starter"} · workspace</span></span>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronsUpDown className="h-4 w-4 shrink-0 text-white/50" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-w-[calc(100vw-2rem)]" data-testid={tid("menu")}>
        {companies.map((c) => <DropdownMenuItem key={c.id} data-testid={tid(`select-${c.id}`)} onSelect={() => select(c.id)} className="gap-2">
          <Building2 className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1"><span className="block truncate">{c.name}</span><span className="text-xs text-muted-foreground">{c.is_owner ? "Owner" : "Team member"} · {c.plan}</span></span>{c.id === company?.id && <Check className="h-4 w-4 text-primary" />}
        </DropdownMenuItem>)}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={tid("add")} onSelect={() => { requestId.current = null; setName(""); setOpen(true); }}><Plus className="mr-2 h-4 w-4" />Add company</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <Dialog open={open} onOpenChange={(value) => { if (!busy) setOpen(value); }}>
      <DialogContent data-testid={tid("create-dialog")} className="max-w-md">
        <DialogHeader><DialogTitle>Add a company</DialogTitle><DialogDescription>Separate investors, updates, team, and billing. New companies start on the free Starter plan.</DialogDescription></DialogHeader>
        <form onSubmit={create} className="space-y-4">
          <div><Label htmlFor={tid("name")}>Company name</Label><Input id={tid("name")} data-testid={tid("name-input")} autoFocus required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} className="mt-2" /></div>
          <p className="text-xs text-muted-foreground" data-testid={tid("create-warning")}>Creating a company switches workspaces. Save any changes first.</p>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" data-testid={tid("create-cancel")} disabled={busy} onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" disabled={busy || !name.trim()} data-testid={tid("create-submit")}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create company</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  </>;
};